import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon, CheckCircleIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
export function SignUpPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const {
    signup
  } = useAuth();
  const navigate = useNavigate();
  const passwordRequirements = [{
    met: password.length >= 8,
    text: 'At least 8 characters'
  }, {
    met: /[A-Z]/.test(password),
    text: 'One uppercase letter'
  }, {
    met: /[0-9]/.test(password),
    text: 'One number'
  }];
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!passwordRequirements.every(req => req.met)) {
      setError('Please meet all password requirements');
      return;
    }
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      const success = signup(name, email, password);
      if (success) {
        navigate('/profile');
      } else {
        setError('An account with this email already exists');
      }
      setLoading(false);
    }, 1000);
  };
  return <main className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center px-4 py-12" data-id="element-611">
      <div className="w-full max-w-md" data-id="element-612">
        <div className="bg-white rounded-2xl shadow-xl p-8" data-id="element-613">
          {/* Logo */}
          <div className="text-center mb-8" data-id="element-614">
            <Link to="/" className="inline-block" data-id="element-615">
              <span className="text-3xl font-bold text-blue-900 italic" data-id="element-616">
                Zinga
              </span>
              <span className="text-xs text-orange-500 block -mt-1" data-id="element-617">
                Login Creatives
              </span>
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 mt-4" data-id="element-618">
              Create Account
            </h1>
            <p className="text-gray-600" data-id="element-619">Join Nakuru's creative community</p>
          </div>

          {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm" data-id="element-620">
              {error}
            </div>}

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-621">
            <div data-id="element-622">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-623">
                Full Name
              </label>
              <div className="relative" data-id="element-624">
                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-625" />
                <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="John Doe" required data-id="element-626" />
              </div>
            </div>

            <div data-id="element-627">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-628">
                Email Address
              </label>
              <div className="relative" data-id="element-629">
                <MailIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-630" />
                <input type="email" id="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="you@example.com" required data-id="element-631" />
              </div>
            </div>

            <div data-id="element-632">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-633">
                Password
              </label>
              <div className="relative" data-id="element-634">
                <LockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-635" />
                <input type={showPassword ? 'text' : 'password'} id="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="••••••••" required data-id="element-636" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-637">
                  {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-638" /> : <EyeIcon className="w-5 h-5" data-id="element-639" />}
                </button>
              </div>
              <div className="mt-2 space-y-1" data-id="element-640">
                {passwordRequirements.map((req, idx) => <div key={idx} className="flex items-center space-x-2 text-sm" data-id="element-641">
                    <CheckCircleIcon className={`w-4 h-4 ${req.met ? 'text-green-500' : 'text-gray-300'}`} data-id="element-642" />
                    <span className={req.met ? 'text-green-600' : 'text-gray-500'} data-id="element-643">
                      {req.text}
                    </span>
                  </div>)}
              </div>
            </div>

            <div data-id="element-644">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-645">
                Confirm Password
              </label>
              <div className="relative" data-id="element-646">
                <LockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-647" />
                <input type={showPassword ? 'text' : 'password'} id="confirmPassword" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="••••••••" required data-id="element-648" />
              </div>
            </div>

            <div className="flex items-start" data-id="element-649">
              <input type="checkbox" id="terms" className="w-4 h-4 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500" required data-id="element-650" />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-600" data-id="element-651">
                I agree to the{' '}
                <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-652">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-653">
                  Privacy Policy
                </a>
              </label>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-3 rounded-lg font-medium transition-colors" data-id="element-654">
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center" data-id="element-655">
            <p className="text-gray-600" data-id="element-656">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium" data-id="element-657">
                Sign in
              </Link>
            </p>
          </div>

          {/* Bonus Info */}
          <div className="mt-6 bg-green-50 rounded-lg p-4 text-center" data-id="element-658">
            <p className="text-green-700 text-sm" data-id="element-659">
              🎉 New accounts start with <strong data-id="element-660">500 KES</strong> in wallet
              balance!
            </p>
          </div>
        </div>
      </div>
    </main>;
}